import { debounce } from "lodash";
import { IoSend } from "react-icons/io5";
import { IoSearch } from "react-icons/io5";
import { useState, useEffect } from "react";
import { FaRegCopy } from "react-icons/fa6";
import { FaCheckCircle } from "react-icons/fa";
import toast from "react-hot-toast";
import { Drawer } from "@material-tailwind/react";
import { useDispatch, useSelector } from "react-redux";
import { shareUserAccount } from "../../redux/ApiSlice/AccountProfileSlice";
import { searchFriends, getRecentFriendList } from "../../redux/ApiSlice/ShareSlice";

export default function ShareModal({ closeDrawerBottom, openBottom, userId }) {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userMessage, setUserMessage] = useState("");
    const [copying, setCopying] = useState(false);

    const handleSelectUser = (user) => {
        setSelectedUsers((prev) => {
            if (prev.some((u) => u.id === user.id)) {
                return prev.filter((u) => u.id !== user.id);
            } else {
                return [...prev, user];
            }
        });
    };

    const debouncedSearch = debounce((query) => {
        if (query.trim() !== "") {
            dispatch(searchFriends(query));
        } else {
            dispatch(getRecentFriendList());
        }
    }, 1000);

    const dispatch = useDispatch();
    const { searchData, recentFriends, loading } = useSelector((state) => state.SharePost);

    useEffect(() => {
        dispatch(getRecentFriendList());
    }, [dispatch]);

    const handleSearch = (query) => {
        setSearchQuery(query);
        debouncedSearch(query);
    };

    // Use recentFriends for default, searchData for search
    const filteredUsers = searchQuery.trim() !== ""
        ? searchData
        : recentFriends;

    // Copy link function (reference from Share.jsx)
    const copyToClipboard = async () => {
        try {
            setCopying(true);
            await navigator.clipboard.writeText(window.location.href);
            toast.success("URL copied to clipboard!");
        } catch (error) {
            console.error("Failed to copy URL:", error);
            toast.error("Failed to copy URL");
        } finally {
            setCopying(false);
        }
    };

    // Handle sending user account to selected users
    const handleSend = async () => {
        if (!userId || selectedUsers.length === 0) {
            toast.error("Select at least one user.");
            return;
        }

        const payload = {
            receiver_ids: selectedUsers.map((u) => u.id),
            user_id: userId,
            custom_message: userMessage || undefined,
        };

        try {
            await dispatch(shareUserAccount(payload)).unwrap();
            toast.success(`Sent to ${selectedUsers.map(u => u.full_name || u.full_name?.split(" ")[0]).join(", ")}`);
            setSelectedUsers([]);
            setUserMessage("");
        } catch (error) {
            toast.error("Failed to send message");
        }
    };

    return (
        <>
            <Drawer
                size={220}
                placement="bottom"
                open={openBottom}
                style={{ zIndex: 1 }}
                onClose={closeDrawerBottom}
                className={`absolute dark:bg-black w-full ${!openBottom && "hidden"}`}
            >
                <div className="grid grid-cols-6 border-t border-gray-800 relative ">
                    <div className={`col-span-6 px-2 ${selectedUsers.length > 0 ? "h-[11rem]" : "h-[13.5rem]"} overflow-y-scroll`}>
                        <div className="mt-2 pb-2">
                            <div className="relative my-2 flex items-center">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    placeholder="Search for friends..."
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="w-full py-1.5 pr-2 pl-7 bg-white dark:bg-gray-900 rounded-md outline-none text-[13px] text-black dark:text-white border-black dark:border-gray-900 border placeholder:text-[13px]"
                                />
                                <IoSearch className="absolute left-1 top-1/2 transform -translate-y-1/2 text-black dark:text-white text-[20px]" />
                                <button
                                    type="button"
                                    title="Copy link"
                                    onClick={copyToClipboard}
                                    className="ml-2 flex items-center justify-center bg-gray-300 dark:bg-gray-900 rounded-full w-[32px] h-[32px] cursor-pointer"
                                >
                                    <FaRegCopy className={`text-black dark:text-white text-[16px] ${copying ? "animate-pulse" : ""}`} />
                                </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                {filteredUsers && filteredUsers.length > 0 && filteredUsers.map((data, index) => (
                                    <div key={index}
                                        onClick={() => handleSelectUser(data)}
                                        className={`flex flex-col items-center cursor-pointer relative`}
                                    >
                                        <img
                                            alt={`img${index}`}
                                            src={data.avatar_url}
                                            className={`h-[50px] w-[50px] object-cover rounded-full cursor-pointer ${selectedUsers.some((u) => u.id === data.id) ? "border-2 border-green-500 rounded-full p-1" : ""}`}
                                        />
                                        <p className="text-[10px] text-black dark:text-white">
                                            {data.full_name?.split(" ")[0]}
                                        </p>
                                        {selectedUsers.some((u) => u.id === data.id) && (
                                            <FaCheckCircle className="absolute top-0 right-0 text-green-500 bg-white rounded-full text-[16px]" />
                                        )}
                                    </div>
                                ))}

                                {filteredUsers && filteredUsers.length === 0 && !loading && (
                                    <p className="text-[12px] text-gray-500 h-[6rem] w-full flex justify-center items-center">
                                        No friends found
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Show custom message input only if users are selected */}
                {selectedUsers.length > 0 && (
                    <div className="absolute bottom-0 w-full left-1/2 transform justify-between -translate-x-1/2 z-50 bg-white dark:bg-black shadow-lg flex items-center px-2 py-2 border-t border-gray-300 dark:border-gray-800">
                        <div className="flex w-full items-center">
                            <input
                                type="text"
                                value={userMessage}
                                placeholder="Send message to selected users..."
                                onChange={(e) => setUserMessage(e.target.value)}
                                className="w-full py-1.5 px-2 rounded-md outline-none text-[13px] bg-white dark:bg-gray-900 text-black dark:text-white border-black dark:border-gray-900 border placeholder:text-[13px]"
                            />
                        </div>
                        <button
                            className="ml-2 bg-black dark:bg-gray-900 text-white px-2 py-2 rounded-full text-[12px] font-bold"
                            onClick={handleSend}
                        >
                            <IoSend className="text-[16px]" />
                        </button>
                    </div>
                )}
            </Drawer>
        </>
    );
}